import type { AnalyticsMetadata } from '@atlassian/jira-common-constants/src/analytics-metadata';
import type { ExternalAction } from '@atlassian/jira-issue-view-store/src/actions/external-actions';
import type { Attributes } from '@atlassian/jira-product-analytics-bridge';
import type { CardId, CardKey } from '../../../../model/types';

export const CARD_CLICK = 'state.actions.cards.selection.CARD_CLICK' as const;

export type CardClick = {
	type: typeof CARD_CLICK;
	payload: {
		cardId: CardId;
		withCmd: boolean;
		withShift: boolean;
		withCheckbox: boolean;
		fireSelectionAnalytics?: (extraAttr: Attributes) => void;
		externalAction?: ExternalAction;
		analyticsMetadata?: AnalyticsMetadata;
	};
};

export const cardClick = (
	cardId: CardId,
	withCmd: boolean,
	withShift: boolean,
	withCheckbox: boolean,
	fireSelectionAnalytics?: (extraAttr: Attributes) => void,
	externalAction?: ExternalAction,
	analyticsMetadata?: AnalyticsMetadata,
) => ({
	type: CARD_CLICK,
	payload: {
		cardId,
		withCmd,
		withShift,
		withCheckbox,
		fireSelectionAnalytics,
		externalAction,
		analyticsMetadata,
	},
});

export const PARENT_CLICK = 'state.actions.cards.selection.PARENT_CLICK' as const;

export type ParentClick = {
	type: typeof PARENT_CLICK;
	payload: {
		cardId: CardId;
	};
};

export const parentClick = (cardId: CardId) => ({
	type: PARENT_CLICK,
	payload: {
		cardId,
	},
});

export const CARD_TOGGLE_SELECTION = 'state.actions.cards.selection.CARD_TOGGLE_SELECTION' as const;

export type CardToggleSelection = {
	type: typeof CARD_TOGGLE_SELECTION;
	payload: {
		cardId: CardId;
	};
};

export const cardToggleSelection = (cardId: CardId) => ({
	type: CARD_TOGGLE_SELECTION,
	payload: {
		cardId,
	},
});

export const CARD_EMPTY_SELECTION = 'state.actions.cards.selection.CARD_EMPTY_SELECTION' as const;

export type CardEmptySelection = {
	type: typeof CARD_EMPTY_SELECTION;
};

export const cardEmptySelection = () => ({
	type: CARD_EMPTY_SELECTION,
});

export const CARD_SINGLE_SELECT = 'state.actions.cards.selection.CARD_SINGLE_SELECT' as const;

export type CardSingleSelect = {
	type: typeof CARD_SINGLE_SELECT;
	payload: {
		cardId: CardId;
	};
};

export const cardSingleSelect = (cardId: CardId) => ({
	type: CARD_SINGLE_SELECT,
	payload: {
		cardId,
	},
});

export const CARD_MULTI_SELECT = 'state.actions.cards.selection.CARD_MULTI_SELECT' as const;

export type CardMultiSelect = {
	type: typeof CARD_MULTI_SELECT;
	payload: {
		cardIds: CardId[];
	};
};

export const cardMultiSelect = (cardIds: CardId[]) => ({
	type: CARD_MULTI_SELECT,
	payload: {
		cardIds,
	},
});

export const SET_LAST_SELECTED_CARD_KEY =
	'state.actions.cards.selection.SET_LAST_SELECTED_CARD_KEY' as const;

export type SetLastSelectedCardKey = {
	type: typeof SET_LAST_SELECTED_CARD_KEY;
	payload: {
		cardKey: CardKey;
	};
};

export const setLastSelectedCardKey = (cardKey: CardKey) => ({
	type: SET_LAST_SELECTED_CARD_KEY,
	payload: {
		cardKey,
	},
});

export const CARD_CLEAR_RELATIVE_CARD =
	'state.actions.cards.selection.CARD_CLEAR_RELATIVE_CARD' as const;

export type CardClearRelativeCard = {
	type: typeof CARD_CLEAR_RELATIVE_CARD;
};

export const cardClearRelativeCard = () => ({
	type: CARD_CLEAR_RELATIVE_CARD,
});

export const CARD_MULTI_SELECT_EXTEND =
	'state.actions.cards.selection.CARD_MULTI_SELECT_EXTEND' as const;

export type CardMultiSelectExtend = {
	type: typeof CARD_MULTI_SELECT_EXTEND;
	payload: {
		cardIds: CardId[];
	};
};

export const cardMultiSelectExtend = (cardIds: CardId[]) => ({
	type: CARD_MULTI_SELECT_EXTEND,
	payload: {
		cardIds,
	},
});

export const CARD_MULTI_REMOVE = 'state.actions.cards.selection.CARD_MULTI_REMOVE' as const;

export type CardMultiRemove = {
	type: typeof CARD_MULTI_REMOVE;
	payload: {
		cardIds: CardId[];
	};
};

export const cardMultiRemove = (cardIds: CardId[]) => ({
	type: CARD_MULTI_REMOVE,
	payload: {
		cardIds,
	},
});

export type Action =
	| CardClick
	| CardToggleSelection
	| CardSingleSelect
	| CardMultiSelect
	| SetLastSelectedCardKey
	| CardClearRelativeCard
	| ParentClick
	| CardMultiSelectExtend
	| CardMultiRemove;
